import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import { useSeries } from '@/contexts/SeriesContext';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  BookOpen,
  Users,
  Sparkles,
  Wand2,
  Loader2,
  Clock,
  ChevronDown,
  ChevronUp,
  Globe,
  History,
  User,
  Star,
  Shield,
  Heart,
  Lightbulb,
  Library,
  Lock,
  Film,
  Mic
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { getBookTypeLabel } from '@/lib/utils';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const roleConfig = {
  protagonist: { icon: Star, color: '#F59E0B', label: 'Protagonist' },
  supporting: { icon: Heart, color: '#EC4899', label: 'Supporting' },
  antagonist: { icon: Shield, color: '#EF4444', label: 'Antagonist' },
  mentor: { icon: Sparkles, color: '#8B5CF6', label: 'Mentor' },
  sidekick: { icon: User, color: '#22C55E', label: 'Sidekick' }
};

// Story theme options
const themeOptions = [
  { value: 'auto', label: 'Auto (from prompt)' },
  { value: 'friendship', label: 'Friendship' },
  { value: 'courage', label: 'Courage' },
  { value: 'kindness', label: 'Kindness' },
  { value: 'perseverance', label: 'Perseverance' },
  { value: 'honesty', label: 'Honesty' },
  { value: 'creativity', label: 'Creativity' },
  { value: 'family', label: 'Family' },
  { value: 'love', label: 'Love' }
];

// Story inspiration options (generic styles)
const inspirationOptions = [
  { value: 'original', label: 'Original Style' },
  { value: 'rhyming_fun', label: 'Rhyming Fun' },
  { value: 'quirky_mischief', label: 'Quirky Mischief' },
  { value: 'gentle_woodland', label: 'Gentle Woodland' },
  { value: 'bold_collage', label: 'Bold Collage' },
  { value: 'simple_expressive', label: 'Simple & Expressive' },
  { value: 'dreamlike_wild', label: 'Dreamlike Wild' },
  { value: 'poetic_whimsy', label: 'Poetic Whimsy' },
  { value: 'other', label: 'Custom...' }
];

const CreateSeriesBookPage = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { currentSeries, fetchSeries, fetchUserSeries, getSeriesContext, seriesLimit, loading: contextLoading } = useSeries();

  const [prompt, setPrompt] = useState('');
  const [storyLength, setStoryLength] = useState('12'); // Default to 12 pages
  const [loading, setLoading] = useState(false);
  const [loadingContext, setLoadingContext] = useState(true);
  const [seriesContext, setSeriesContext] = useState(null);
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [activeJobsCount, setActiveJobsCount] = useState(0);
  const [premiumStatusLoaded, setPremiumStatusLoaded] = useState(false);

  // Fetch user series to get premium status on mount
  useEffect(() => {
    const loadPremiumStatus = async () => {
      await fetchUserSeries();
      setPremiumStatusLoaded(true);
    };
    loadPremiumStatus();
  }, [fetchUserSeries]);

  // Redirect non-premium users to the series list (which shows the preview)
  // Wait for premium status to be loaded before checking
  useEffect(() => {
    if (premiumStatusLoaded && seriesLimit && !seriesLimit.isPremium) {
      toast.error('Series is a premium feature', {
        description: 'Upgrade to Premium to create story series'
      });
      navigate('/series');
    }
  }, [premiumStatusLoaded, seriesLimit, navigate]);

  // Story version selection
  // v1 = Standard (Flux), v2 = Premium (Gemini 2.5), v3 = Pro (Gemini 3)
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Story customization options
  const [selectedTheme, setSelectedTheme] = useState('auto');
  const [featuredCharacters, setFeaturedCharacters] = useState([]);
  const [storyInspiration, setStoryInspiration] = useState('original');
  const [customInspiration, setCustomInspiration] = useState('');
  const [enableAnimations, setEnableAnimations] = useState(false);
  // Narrator voice selection for premium users
  const [narratorVoice, setNarratorVoice] = useState("nova");

  // Expandable sections for sidebar
  const [showCharacters, setShowCharacters] = useState(true);
  const [showPreviousBooks, setShowPreviousBooks] = useState(false);
  const [showWorld, setShowWorld] = useState(false);


  // Calculate credit cost based on version (v1=1, v2=1, v3=2) + animations (+1)
  const getStoryCost = () => {
    let cost = selectedVersion === 3 ? 2 : 1;
    if (enableAnimations) cost += 1;
    return cost;
  };

  // Get available versions based on user tier
  // Series books require v2 minimum, so free users without purchases can't create series books
  const getAvailableVersions = () => {
    if (user?.is_premium) {
      return [2, 3]; // Premium can use v2 or v3 (no v1 for series)
    } else if (user?.has_purchased_credits) {
      return [2]; // Users who purchased credits can use v2 for series
    }
    return []; // Free users cannot create series books (require v2+)
  };

  // Get default version based on user tier
  const getDefaultVersion = () => {
    if (user?.is_premium) return 2;
    if (user?.has_purchased_credits) return 2;
    return 2; // Default to v2, but UI should block free users
  };

  // Toggle character in featured list
  const toggleCharacter = (charId) => {
    setFeaturedCharacters(prev =>
      prev.includes(charId)
        ? prev.filter(id => id !== charId)
        : [...prev, charId]
    );
  };

  useEffect(() => {
    loadData();
  }, [seriesId]);

  // Set default version when user loads
  useEffect(() => {
    if (user && selectedVersion === null) {
      setSelectedVersion(getDefaultVersion());
    }
  }, [user]);

  // Pre-select all characters when series loads
  useEffect(() => {
    if (currentSeries?.characters?.length > 0 && featuredCharacters.length === 0) {
      setFeaturedCharacters(currentSeries.characters.map(c => c.id));
    }
  }, [currentSeries]);

  // Set appropriate default story length when series loads (based on age range)
  useEffect(() => {
    if (currentSeries?.age_range && defaultStoryLength[currentSeries.age_range]) {
      setStoryLength(defaultStoryLength[currentSeries.age_range]);
    }
  }, [currentSeries?.age_range]);

  const loadData = async () => {
    setLoadingContext(true);
    try {
      // Fetch user, series, context, and active jobs in parallel
      const [userRes, seriesData, contextData, jobsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        fetchSeries(seriesId),
        getSeriesContext(seriesId),
        axios.get(`${API}/v1/jobs/mine`, { withCredentials: true })
      ]);

      setUser(userRes.data);
      setSeriesContext(contextData);

      // Count active jobs for this series
      const activeJobs = (jobsRes.data.jobs || []).filter(
        job => job.series_id === seriesId && ['queued', 'processing'].includes(job.status)
      );
      setActiveJobsCount(activeJobs.length);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load series information');
    } finally {
      setLoadingContext(false);
    }
  };

  // Age-based story length options (matches backend job_service.py)
  const storyLengthOptions = {
    "3-5": [
      { value: "8", label: "8 pages", pages: 8 },
      { value: "10", label: "10 pages", pages: 10 },
      { value: "12", label: "12 pages", pages: 12 },
      { value: "14", label: "14 pages", pages: 14 }
    ],
    "5-8": [
      { value: "10", label: "10 pages", pages: 10 },
      { value: "12", label: "12 pages", pages: 12 },
      { value: "14", label: "14 pages", pages: 14 },
      { value: "16", label: "16 pages", pages: 16 }
    ],
    "8-12": [
      { value: "20", label: "20 pages", pages: 20 },
      { value: "30", label: "30 pages", pages: 30 },
      { value: "40", label: "40 pages", pages: 40 }
    ]
  };

  // Default page counts: 12 for picture books/easy readers, 20 for chapter books
  const defaultStoryLength = {
    "3-5": "12",
    "5-8": "12",
    "8-12": "20"
  };

  const currentLengthOptions = storyLengthOptions[currentSeries?.age_range] || storyLengthOptions["5-8"];

  const getPageCount = () => {
    const option = currentLengthOptions.find(o => o.value === storyLength);
    return option ? option.pages : currentLengthOptions[1].pages;
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter what happens in this book!");
      return;
    }

    setLoading(true);

    try {
      // Create story with series context
      const payload = {
        prompt: prompt.trim(),
        age_range: currentSeries.age_range,
        category: 'series',
        is_public: true, // Series books default to public
        series_id: seriesId,
        story_length: storyLength,
        num_pages: getPageCount(),
        art_style: currentSeries.art_style,
        color_palette: currentSeries.color_palette,
        narrative_tone: currentSeries.narrative_tone,
        // Story version (v2 or v3)
        story_version: selectedVersion || getDefaultVersion(),
        // Use Enhanced pipeline for series (better character consistency)
        use_v2_pipeline: true,
        // Animation option (premium only)
        enable_animations: enableAnimations,
        // Narrator voice (premium only)
        narrator_voice: narratorVoice,
        // Story customization options
        custom_theme: selectedTheme && selectedTheme !== 'auto' ? selectedTheme : undefined,
        story_inspiration: storyInspiration,
        custom_inspiration: storyInspiration === 'other' ? customInspiration : undefined,
        // Include series context for the AI with featured characters
        series_context: {
          ...seriesContext,
          featured_character_ids: featuredCharacters.length > 0 ? featuredCharacters : undefined
        }
      };

      const response = await axios.post(`${API}/v1/jobs`, payload, {
        withCredentials: true
      });

      const { job_id, queue_position } = response.data;

      if (queue_position > 1) {
        toast.info(`You're #${queue_position} in queue. Your book will appear when ready!`);
      } else {
        toast.success("Creating your book! You can track progress here.");
      }

      // Navigate back to series page to see the book loading
      navigate(`/series/${seriesId}`, { state: { newJobId: job_id } });
    } catch (error) {
      console.error("Error creating story job:", error);

      // Extract error message - handle Pydantic validation errors
      const getErrorMessage = (err) => {
        const detail = err.response?.data?.detail;
        if (!detail) return "Failed to create story. Please try again.";
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) {
          // Pydantic validation error array
          return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
        if (typeof detail === 'object' && detail.msg) {
          return detail.msg;
        }
        return "Failed to create story. Please try again.";
      };

      if (error.response?.status === 401) {
        toast.error("Please login to create stories");
      } else if (error.response?.status === 403) {
        toast.error(getErrorMessage(error), {
          action: { label: "Add Credits", onClick: () => navigate('/pricing') }
        });
      } else {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingContext) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-60px)] md:min-h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading series context...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!currentSeries) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-60px)] md:min-h-screen">
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
            <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
              Series Not Found
            </p>
            <button
              onClick={() => navigate('/series')}
              className="text-sm hover:underline"
              style={{ color: 'var(--st-primary)' }}
            >
              Back to Series
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const nextBookNumber = (currentSeries.book_count || 0) + activeJobsCount + 1;

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-xl mx-auto px-4 py-4 md:py-6 md:px-8">

          {/* Page Header - Full Width (matching CreatePage) */}
          <div className="rounded-2xl shadow-sm p-5 flex items-center justify-between mb-6" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/series/${seriesId}`)}
                className="p-2 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Create Book {nextBookNumber}
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  in "{currentSeries.title}"
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/series/${seriesId}`)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-indigo-100 dark:hover:bg-indigo-900/30 dark:bg-indigo-900/20"
                style={{ background: '#EEF2FF', color: '#6366F1', fontFamily: 'Quicksand, sans-serif', border: '1px solid var(--st-border-light)' }}
              >
                <Library className="w-4 h-4" />
                Series Details
              </button>
              <span className="text-sm font-medium px-3 py-1.5 rounded-lg" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                {user?.credits || 0} credits
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Main Form - Left Column */}
            <div className="lg:col-span-2 space-y-4">

              {/* Header Row with Version Selector (matching CreatePage) */}
              <div className="flex items-center justify-between">
                {/* Series Style Tags */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}>
                    {getBookTypeLabel(currentSeries.age_range)}
                  </span>
                  <span className="px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}>
                    {currentSeries.art_style || 'storybook'}
                  </span>
                  <span className="px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}>
                    {currentSeries.narrative_tone || 'warm'} tone
                  </span>
                </div>

                {/* Version Dropdown (matching CreatePage) */}
                <Select
                  value={(selectedVersion || getDefaultVersion()).toString()}
                  onValueChange={(value) => {
                    const version = parseInt(value);
                    if (!getAvailableVersions().includes(version)) {
                      if (version === 3) {
                        toast.info("v3 Pro requires Premium subscription", {
                          action: { label: "Upgrade", onClick: () => navigate('/pricing') }
                        });
                      }
                      return;
                    }
                    setSelectedVersion(version);
                  }}
                  disabled={loading}
                >
                  <SelectTrigger
                    className="w-auto min-w-[70px] h-9 border-0 focus:ring-0"
                    style={{ background: 'var(--st-bg-muted)', fontFamily: 'Quicksand, sans-serif' }}
                  >
                    <SelectValue>
                      <div className="flex items-center gap-1">
                        {(selectedVersion || getDefaultVersion()) === 3 && (
                          <Star className="w-3 h-3" style={{ color: '#7C3AED' }} />
                        )}
                        <span
                          className="text-sm font-semibold"
                          style={{
                            color: (selectedVersion || getDefaultVersion()) === 2 ? '#3B82F6' : '#7C3AED'
                          }}
                        >
                          v{selectedVersion || getDefaultVersion()}
                        </span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: '#3B82F6' }}>v2</span>
                        <span className="text-xs" style={{ color: '#3B82F6' }}>Premium</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="3" disabled={!getAvailableVersions().includes(3)}>
                      <div className="flex items-center gap-2">
                        <Star className="w-3 h-3" style={{ color: getAvailableVersions().includes(3) ? '#7C3AED' : '#9CA3AF' }} />
                        <span className="font-semibold" style={{ color: getAvailableVersions().includes(3) ? '#7C3AED' : '#9CA3AF' }}>v3</span>
                        <span className="text-xs" style={{ color: getAvailableVersions().includes(3) ? '#7C3AED' : '#9CA3AF' }}>Pro</span>
                        {!getAvailableVersions().includes(3) && <Lock className="w-3 h-3 text-gray-400" />}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Main Card - Story Input (matching CreatePage) */}
              <div className="rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                {/* Story Idea */}
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}>
                        <Wand2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-bold text-sm sm:text-base" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                        What happens in Book {nextBookNumber}?
                      </span>
                    </div>
                    <span className="text-xs ml-auto sm:ml-0" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      {prompt.length}/500
                    </span>
                  </div>

                  <Textarea
                    placeholder={`Continue the adventure! What happens to ${currentSeries.characters?.[0]?.name || 'your characters'} this time?`}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value.slice(0, 500))}
                    className="min-h-[120px] text-base border-0 focus:ring-0 rounded-xl resize-none p-4"
                    disabled={loading}
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      background: 'var(--st-bg-input)',
                      fontSize: '15px',
                      lineHeight: '1.6'
                    }}
                  />
                </div>

                {/* Quick Settings - Inline (matching CreatePage) */}
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex flex-wrap items-center gap-2 sm:gap-3">
                  {/* Story Length */}
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Clock className="w-4 h-4 hidden sm:block" style={{ color: 'var(--st-text-muted)' }} />
                    <Select value={storyLength} onValueChange={setStoryLength} disabled={loading}>
                      <SelectTrigger className="w-auto min-w-[100px] sm:min-w-[130px] border-0 h-8 sm:h-9 text-xs sm:text-sm font-medium rounded-lg" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentLengthOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Theme */}
                  <Select value={selectedTheme} onValueChange={setSelectedTheme} disabled={loading}>
                    <SelectTrigger className="w-auto min-w-[100px] sm:min-w-[120px] border-0 h-8 sm:h-9 text-xs sm:text-sm font-medium rounded-lg" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                      <SelectValue>{themeOptions.find(t => t.value === selectedTheme)?.label || 'Auto'}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {themeOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Spacer - hidden on mobile */}
                  <div className="hidden sm:block flex-1" />

                  {/* Divider - hidden on mobile */}
                  <div className="hidden sm:block h-6 w-px" style={{ background: 'var(--st-border-light)' }} />

                  {/* Story Inspiration (matching CreatePage) */}
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Lightbulb className="w-4 h-4 hidden sm:block" style={{ color: 'var(--st-text-muted)' }} />
                    <span className="text-xs sm:text-sm hidden sm:inline" style={{ color: 'var(--st-text-muted)' }}>Inspired by</span>
                    <Select value={storyInspiration} onValueChange={setStoryInspiration} disabled={loading}>
                      <SelectTrigger className="w-[110px] sm:w-[130px] border-0 h-8 sm:h-9 text-xs sm:text-sm font-medium rounded-lg" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="original">Original</SelectItem>
                        <SelectItem value="rhyming_fun">Rhyming Fun</SelectItem>
                        <SelectItem value="imaginative_adventure">Imaginative Adventure</SelectItem>
                        <SelectItem value="comic_silly">Comic Silly</SelectItem>
                        <SelectItem value="gentle_woodland">Gentle Woodland</SelectItem>
                        <SelectItem value="bold_collage">Bold Collage</SelectItem>
                        <SelectItem value="simple_expressive">Simple & Expressive</SelectItem>
                        <SelectItem value="quirky_mischief">Quirky Mischief</SelectItem>
                        <SelectItem value="poetic_whimsy">Poetic Whimsy</SelectItem>
                        <SelectItem value="dreamlike_wild">Dreamlike Wild</SelectItem>
                        <SelectItem value="cool_positive">Cool & Positive</SelectItem>
                        <SelectItem value="curious_explorer">Curious Explorer</SelectItem>
                        <SelectItem value="bedtime_cozy">Bedtime Cozy</SelectItem>
                        <SelectItem value="other">Other...</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Custom inspiration input - shows when "other" is selected */}
                {storyInspiration === 'other' && (
                  <div className="px-5 pb-4 animate-in slide-in-from-top-1 duration-200">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--st-text-muted)' }}>Custom inspiration:</span>
                      <input
                        type="text"
                        value={customInspiration}
                        onChange={(e) => setCustomInspiration(e.target.value.slice(0, 200))}
                        placeholder="e.g., gentle bedtime story, silly animal adventure..."
                        className="flex-1 border rounded-lg px-3 h-9 text-sm"
                        style={{ borderColor: 'var(--st-border-light)', color: 'var(--st-text-heading)', background: 'var(--st-bg-input)' }}
                        disabled={loading}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Version + Premium Options Row */}
              <div
                className="flex items-stretch rounded-xl overflow-hidden"
                style={{ border: '1px solid var(--st-border-light)' }}
              >
                {/* Version Badge */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 cursor-help ${
                        (selectedVersion || getDefaultVersion()) === 3
                          ? 'bg-purple-100 dark:bg-purple-900/40'
                          : 'bg-blue-100 dark:bg-blue-900/40'
                      }`}
                    >
                      {(selectedVersion || getDefaultVersion()) === 3 ? (
                        <Star className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )}
                      <span className={`text-sm font-semibold ${
                        (selectedVersion || getDefaultVersion()) === 3
                          ? 'text-purple-700 dark:text-purple-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}>
                        {(selectedVersion || getDefaultVersion()) === 3 ? 'v3 Pro' : 'v2 Premium'}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      {(selectedVersion || getDefaultVersion()) === 3
                        ? 'Best quality with superior character consistency across all pages'
                        : 'High-quality illustrations with good character matching'}
                    </p>
                  </TooltipContent>
                </Tooltip>

                {/* Animations Toggle (Premium only, not for chapter books) */}
                {user?.is_premium && currentSeries?.age_range !== "8-12" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 border-l transition-all cursor-help ${
                        enableAnimations ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-50 dark:bg-gray-800/30'
                      }`} style={{ borderColor: 'var(--st-border-light)' }}>
                        <Film className="w-4 h-4" style={{ color: enableAnimations ? '#D97706' : 'var(--st-text-muted)' }} />
                        <span className={`text-sm font-medium ${enableAnimations ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-400'}`}>
                          Animations
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          enableAnimations ? 'bg-amber-200 text-amber-700' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                        }`}>
                          +1
                        </span>
                        <Switch
                          checked={enableAnimations}
                          onCheckedChange={setEnableAnimations}
                          className="data-[state=checked]:bg-amber-500 scale-90"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Key scenes gently animate — perfect for bedtime reading</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Voice Selector (Premium only) */}
                {user?.is_premium && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 border-l bg-gray-50 dark:bg-gray-800/30 cursor-help" style={{ borderColor: 'var(--st-border-light)' }}>
                        <Mic className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Voice</span>
                        <Select value={narratorVoice} onValueChange={setNarratorVoice}>
                          <SelectTrigger className="w-20 h-7 text-sm border-0 bg-transparent p-0 pl-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nova">Sarah</SelectItem>
                            <SelectItem value="shimmer">Grace</SelectItem>
                            <SelectItem value="onyx">Oliver</SelectItem>
                            <SelectItem value="echo">Max</SelectItem>
                            <SelectItem value="alloy">Riley</SelectItem>
                            <SelectItem value="fable">Pip</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Choose the narrator voice for "Read to Me" audio</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Generate Button (matching CreatePage) */}
              {(() => {
                const canGenerate = prompt.trim() && featuredCharacters.length > 0;
                return (
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !canGenerate}
                    className="w-full flex items-center justify-center gap-2 group relative overflow-hidden"
                    style={{
                      background: canGenerate
                        ? 'linear-gradient(135deg, #818CF8 0%, #6366F1 50%, #4F46E5 100%)'
                        : 'var(--st-bg-muted)',
                      color: canGenerate ? 'white' : 'var(--st-text-muted)',
                      padding: '0.75rem 1.5rem',
                      fontSize: '1rem',
                      boxShadow: canGenerate ? '0 6px 20px rgba(99, 102, 241, 0.35)' : 'none',
                      border: 'none',
                      borderRadius: '12px',
                      fontFamily: 'Chewy, cursive',
                      cursor: canGenerate ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                    }}
                  >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
                    <span>Create Book {nextBookNumber}</span>
                    <span
                      className="ml-1 px-2 py-0.5 rounded-full text-sm font-medium"
                      style={{
                        background: 'rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      {getStoryCost()} {getStoryCost() === 1 ? 'credit' : 'credits'}
                    </span>
                  </>
                  )}
                  </button>
                );
              })()}

              {/* AI Disclaimer (matching CreatePage) */}
              <div className="mt-4 px-4 py-3 rounded-xl" style={{ background: 'var(--st-bg-elevated)', border: '1px solid var(--st-border-light)' }}>
                <p className="text-xs text-center leading-relaxed" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  <span className="font-semibold">AI-Generated Content:</span> The AI will reference your series characters and previous books for continuity. Please review stories before sharing with children.
                </p>
              </div>
            </div>

            {/* Context Panel - Right Column */}
            <div className="lg:col-span-1 space-y-4">

              {/* Characters */}
              <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <button
                  onClick={() => setShowCharacters(!showCharacters)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                    <span className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      Characters ({currentSeries.characters?.length || 0})
                    </span>
                  </div>
                  {showCharacters ? (
                    <ChevronUp className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                  )}
                </button>

                {showCharacters && currentSeries.characters?.length > 0 && (
                  <div className="px-4 pb-4 space-y-2">
                    <p className="text-xs mb-2" style={{ color: 'var(--st-text-muted)' }}>
                      Click to select characters for this book
                    </p>
                    {[...currentSeries.characters]
                      .sort((a, b) => {
                        // Protagonists first, then by name
                        const roleOrder = { protagonist: 0, sidekick: 1, supporting: 2, mentor: 3, antagonist: 4 };
                        const aOrder = roleOrder[a.role] ?? 5;
                        const bOrder = roleOrder[b.role] ?? 5;
                        if (aOrder !== bOrder) return aOrder - bOrder;
                        return (a.name || '').localeCompare(b.name || '');
                      })
                      .map((char) => {
                      const role = roleConfig[char.role] || roleConfig.supporting;
                      const RoleIcon = role.icon;
                      const isSelected = featuredCharacters.includes(char.id);
                      return (
                        <button
                          key={char.id}
                          onClick={() => toggleCharacter(char.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all hover:scale-[1.02] ${isSelected ? 'ring-2 ring-offset-1 dark:ring-offset-[#2A2118]' : ''}`}
                          style={{
                            background: isSelected ? 'var(--st-bg-surface)' : 'var(--st-bg-muted)',
                            border: isSelected ? '2px solid var(--st-primary)' : '2px solid transparent',
                            ringColor: isSelected ? 'var(--st-primary)' : undefined
                          }}
                        >
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 relative">
                            {char.portrait_url ? (
                              <img src={getImageUrl(char.portrait_url)} alt={char.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--st-border-light)' }}>
                                <User className="w-5 h-5" style={{ color: 'var(--st-primary-hover)' }} />
                              </div>
                            )}
                            {isSelected && (
                              <div
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                                style={{ background: 'var(--st-primary)' }}
                              >
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-orange-700 dark:text-orange-300' : ''}`} style={{ color: isSelected ? undefined : 'var(--st-text-heading)' }}>
                              {char.name}
                            </p>
                            <div className="flex items-center gap-1">
                              <RoleIcon className="w-3 h-3" style={{ color: role.color }} />
                              <span className="text-xs" style={{ color: role.color }}>{role.label}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    {featuredCharacters.length === 0 && (
                      <p className="text-xs text-center mt-2" style={{ color: '#F59E0B' }}>
                        Select at least one character
                      </p>
                    )}
                  </div>
                )}

                {showCharacters && (!currentSeries.characters || currentSeries.characters.length === 0) && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-center py-2" style={{ color: 'var(--st-text-muted)' }}>
                      No characters defined yet
                    </p>
                  </div>
                )}
              </div>

              {/* Previous Books */}
              {seriesContext?.book_summaries?.length > 0 && (
                <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  <button
                    onClick={() => setShowPreviousBooks(!showPreviousBooks)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="flex items-center gap-2">
                      <History className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                      <span className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                        Previous Books ({seriesContext.book_summaries.length})
                      </span>
                    </div>
                    {showPreviousBooks ? (
                      <ChevronUp className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                    ) : (
                      <ChevronDown className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                    )}
                  </button>

                  {showPreviousBooks && (
                    <div className="px-4 pb-4 space-y-3">
                      {seriesContext.book_summaries.map((book, index) => (
                        <div
                          key={book.story_id || index}
                          className="p-3 rounded-lg"
                          style={{ background: 'var(--st-bg-muted)' }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                              style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)' }}
                            >
                              {book.book_number}
                            </span>
                            <span className="text-sm font-semibold truncate" style={{ color: 'var(--st-text-heading)' }}>
                              {book.title}
                            </span>
                          </div>

                          {book.key_events?.length > 0 && (
                            <p className="text-xs mt-1" style={{ color: 'var(--st-text-muted)' }}>
                              {book.key_events.slice(0, 2).join(' • ')}
                            </p>
                          )}

                          {book.unresolved_threads?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-semibold" style={{ color: '#F59E0B' }}>
                                Open threads:
                              </p>
                              <ul className="text-xs mt-0.5 space-y-0.5">
                                {book.unresolved_threads.slice(0, 2).map((thread, i) => (
                                  <li key={i} style={{ color: 'var(--st-text-muted)' }}>• {thread}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* World */}
              {currentSeries.world && (() => {
                const hasDetails = currentSeries.world.description || currentSeries.world.locations?.length > 0;
                return (
                  <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                    {hasDetails ? (
                      // Expandable version - has description or locations to show
                      <>
                        <button
                          onClick={() => setShowWorld(!showWorld)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                            <span className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                              {currentSeries.world.name}
                            </span>
                          </div>
                          {showWorld ? (
                            <ChevronUp className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                          ) : (
                            <ChevronDown className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                          )}
                        </button>

                        {showWorld && (
                          <div className="px-4 pb-4">
                            {currentSeries.world.description && (
                              <p className="text-sm mb-2" style={{ color: 'var(--st-text-muted)' }}>
                                {currentSeries.world.description}
                              </p>
                            )}

                            {currentSeries.world.locations?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {currentSeries.world.locations.map((loc, i) => (
                                  <span
                                    key={i}
                                    className="px-2 py-0.5 rounded-full text-xs"
                                    style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)' }}
                                  >
                                    {loc}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // Static version - just the world name, no expandable content
                      <div className="flex items-center gap-2 p-4">
                        <Globe className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                        <span className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                          {currentSeries.world.name}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Credits Info */}
              <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold" style={{ color: 'var(--st-text-heading)' }}>Your Credits</span>
                  <span className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-primary)' }}>
                    {user?.credits || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CreateSeriesBookPage;
